import {createAsyncThunk} from '@reduxjs/toolkit'

import {isApiError} from '@shared/helpers/api'

import {
    BranchDeliveryScheduleType,
    DeliveryTagTypeValues,
    OrderSheetPageSet,
    OrderSheetPageSetType,
    ReasonType,
} from '@web/core/constants'
import getApi, {createApiAsyncThunk, createWrapper, getBaseUrl} from '@web/helpers/api'
import {CommonState, pageSetInfoSelector} from '@web/slices/common/slice'
import {addressesInfoSelector, DeliveryInfoState} from '@web/slices/deliveryInfo/slice'
import {
    arrivalGuaranteeInfoSelector,
    externalBranchDeliverySelector,
    orderSheetProductInfoSelector,
    OrderSheetProductsState,
} from '@web/slices/orderSheetProducts/slice'
import {OrderTotalState} from '@web/slices/orderTotal/slice'
import {PointPaymentState} from '@web/slices/pointPayment/slice'

import api from './apis'
import {getArrivalGuaranteeChangedInfo, getChangeDeliveryAreaExtraFeeMessage} from './deliveryHelper'
import {
    ArrivalGuaranteeValidateResponse,
    DeliveriesAddressFeeResponse,
    DeliveryError,
    ValidateBranchDeliveryResponse,
} from './interfaces'

/** 변경 후 재계산 요청 */
export const getDeliveryAddressFee = createAsyncThunk<
    {
        data: DeliveriesAddressFeeResponse
        arrivalGuaranteeInfo: Maybe<{
            isChanged: boolean
            arrivalDateChangedProductIds: string[]
        }>
        changeDeliveryAreaExtraFeeMessage: string
    },
    {
        pageSet: OrderSheetPageSetType
    },
    {
        state: {
            common: CommonState
            deliveryInfo: DeliveryInfoState
            orderSheetProducts: OrderSheetProductsState
            orderTotal: OrderTotalState
            pointPayment: PointPaymentState
        }
        rejectValue: DeliveryError & {
            exception?: {
                addressId: string
                shouldReloadAddress: boolean
                fallbackMessage: string
            }
        }
    }
>('delivery/getDeliveryAddressFee', async ({pageSet}, {getState, rejectWithValue}) => {
    try {
        const state = getState()
        const {deliveryAddress} = addressesInfoSelector(state)
        const {hasArrivalGuaranteeProduct} = orderSheetProductInfoSelector(state)
        const {arrivalGuaranteesByOrderSheetProductId} = arrivalGuaranteeInfoSelector(state)
        const {orderSheetPageSet} = pageSetInfoSelector(state)

        if (hasArrivalGuaranteeProduct && deliveryAddress.zipCode.length === 6) {
            const errorMessage = '선택한 주소는 N배송 상품 정보를 확인할 수 없습니다. 도로명 주소로 다시 입력해주세요.'
            return rejectWithValue({
                title: '',
                status: 400,
                detail: '',
                fallbackMessage: errorMessage,
                code: 'VALIDATION_FAILED',
                validationFailure: {
                    message: errorMessage,
                    fallbackMessage: errorMessage,
                    reasonType: ReasonType.REJECT_SIX_DIGITS_ZIPCODE_WITH_ARRIVAL_GUARANTEE_PRODUCT,
                    reasonAttributes: {},
                },
            })
        }

        const params = {
            orderSheetId: state.common.seqID,
            address: deliveryAddress.baseAddress,
            zipCode: deliveryAddress.zipCode,
            buildingManagementNo: deliveryAddress.buildingManagementNo,
            locationX: deliveryAddress.locationX,
            locationY: deliveryAddress.locationY,
            arrivalGuaranteesByOrderSheetProductId: hasArrivalGuaranteeProduct
                ? arrivalGuaranteesByOrderSheetProductId
                : null,
        }
        const result = await api.getDeliveryAddressFee(pageSet, params)

        const deliveryFeeAmount = result.delivery.totalPrepayOrderDeliveryFee
        const arrivalGuaranteeInfo = getArrivalGuaranteeChangedInfo(getState(), {
            updatedArrivalGuaranteesByOrderSheetProductId: result.updatedArrivalGuaranteesByOrderSheetProductId,
            deliveryFeeAmount,
        })

        return {
            data: result,
            arrivalGuaranteeInfo,
            changeDeliveryAreaExtraFeeMessage: getChangeDeliveryAreaExtraFeeMessage(state, {
                deliveryFeeAmount,
                shouldArrivalGuaranteeChange: arrivalGuaranteeInfo?.isChanged ?? false,
                shouldMoreDetailMessage: orderSheetPageSet === 'SELLER_CHECKOUT',
            }),
        }
    } catch (e) {
        if (isApiError(e)) {
            return rejectWithValue(e.response?.data?.result)
        }
        throw e
    }
})

/** 가주문 생성 전 도착보장 상품의 배송정보 변경 여부 체크 */
export const getArrivalGuaranteeValidate = createAsyncThunk<
    {
        data: ArrivalGuaranteeValidateResponse
        arrivalGuaranteeInfo: Maybe<{
            isChanged: boolean
            arrivalDateChangedProductIds: string[]
            deliveryTagType?: DeliveryTagTypeValues
        }>
    },
    void,
    {
        state: {
            common: CommonState
            deliveryInfo: DeliveryInfoState
            orderSheetProducts: OrderSheetProductsState
            orderTotal: OrderTotalState
        }
        rejectValue: DeliveryError & {
            exception?: {
                addressId: string
                shouldReloadAddress: boolean
                fallbackMessage: string
            }
        }
    }
>('delivery/getArrivalGuaranteeValidate', async (_, {getState, rejectWithValue}) => {
    try {
        const state = getState()
        const {deliveryAddress} = addressesInfoSelector(state)
        const {arrivalGuaranteesByOrderSheetProductId} = arrivalGuaranteeInfoSelector(state)
        const params = {
            orderSheetId: state.common.seqID,
            zipCode: deliveryAddress.zipCode,
            buildingManagementNo: deliveryAddress.buildingManagementNo,
            locationX: deliveryAddress.locationX,
            locationY: deliveryAddress.locationY,
            arrivalGuaranteesByOrderSheetProductId,
            isSubscription: state.common.pageSet === 'SHOPPING_SUBSCRIPTION',
        }

        const result = await api.getArrivalGuaranteeValidate(params)
        return {
            data: result,
            arrivalGuaranteeInfo: getArrivalGuaranteeChangedInfo(getState(), {
                updatedArrivalGuaranteesByOrderSheetProductId: result.updatedArrivalGuaranteesByOrderSheetProductId,
            }),
        }
    } catch (e) {
        if (isApiError(e)) {
            return rejectWithValue(e.response?.data?.result)
        }
        throw e
    }
})

/**
 * 결제하기 전 컬리 배송정보 변경 여부 검사
 * 배송정보가 변경된 경우에만 배송정보 응답
 */
export const getValidateBranchDelivery = createApiAsyncThunk<
    ValidateBranchDeliveryResponse,
    void,
    {
        state: {
            common: CommonState
            deliveryInfo: DeliveryInfoState
            orderSheetProducts: OrderSheetProductsState
        }
        rejectValue: string
    }
>('delivery/getValidateBranchDelivery', async (_, {getState, rejectWithValue}) => {
    const state = getState()
    const orderSheetId = state.common.seqID
    const {deliveryAddress} = addressesInfoSelector(state)
    const externalBranchDelivery = externalBranchDeliverySelector(state)

    if (!orderSheetId || !externalBranchDelivery || !externalBranchDelivery.lawFormatted) {
        return rejectWithValue('API를 호출할 때 필요한 필수 정보가 없음')
    }

    const {
        deliveryScheduleType,
        lawFormatted: {branchDeliveryDate, branchDeliveryEndTime},
    } = externalBranchDelivery

    const branchDeliveryRequestBody: {
        /** 기본 배송지 */
        baseAddress: string
        /** 우편번호 */
        zipCode: string
        /** 장보기 외부업체 배송정보 */
        branchExternalDelivery: {
            /** 배송 예정일자 (yyyyMMdd) */
            branchDeliveryDate: string
            /** 배송예정시각 (HHmm) */
            branchDeliveryEndTime: string
            /** 배송 일정 유형 */
            deliveryScheduleType: BranchDeliveryScheduleType
        }
    } = {
        /** 기본 배송지 */
        baseAddress: deliveryAddress.baseAddress,
        /** 우편번호 */
        zipCode: deliveryAddress.zipCode,
        /** 장보기 외부업체 배송정보 */
        branchExternalDelivery: {
            deliveryScheduleType,
            branchDeliveryDate,
            branchDeliveryEndTime,
        },
    }

    const baseUrl = getBaseUrl(OrderSheetPageSet.SELLER)

    return createWrapper<ValidateBranchDeliveryResponse>(
        getApi(baseUrl).post('/orderSheet/seller/validateBranchDelivery', {
            orderSheetId,
            branchDeliveryRequestBody,
        }),
    )
})
